import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { useCallback, useEffect, useState } from 'react';
import { generatePath } from 'react-router';

import { ClientTPRMApi } from 'Api/TPRM/TPRMApi';
import { Link } from 'Components/Buttons/Buttons';
import { ConfirmationModal } from 'Components/Modal/ConfirmationModal';
import { PageLayoutDefault } from 'Components/PageLayout/PageLayoutDefault';
import { Placeholder } from 'Components/Placeholder/Placeholder';
import { Text } from 'Components/Text/Text';
import { GENERIC_ERROR_MESSAGE } from 'Config/Errors';
import { ICON_ADD_CREATE } from 'Config/Icons';
import { CREATE_THIRD_PARTY, CREATE_THIRD_PARTY_SERVICE } from 'Config/PathTemplates';
import { AuthHandler } from 'Helpers/Auth/AuthHandler';
import { loggedInUserHasFullAccessToTprm } from 'Helpers/UserUtils';
import { FinalizationState, ScheduleV2, ServiceForNonTprmUser, ThirdPartyContact, ThirdPartyResponseWithServices, ThirdPartyService, ThirdPartyWithServicesForNonTprmUser } from 'Models/TPRM';

import { ServiceAssessmentDueDateModal } from '../ServiceAssessmentDueDateModal/ServiceAssessmentDueDateModal';
import { ThirdPartiesAndServicesTable, ThirdPartiesAndServicesTableProps } from './Components/ThirdPartiesAndServicesTable/ThirdPartiesAndServicesTable';
import { FinalizeThirdPartyServiceModal } from '../Components/FinalizeThirdPartyServiceModal/FinalizeThirdPartyServiceModal';
import { UsersModal } from '../Components/UsersModal/UsersModal';
import { ThirdPartyContactModal } from '../ServiceDashboard/ThirdPartyContactModal/ThirdPartyContactModal';
import { ServiceInherentRiskQuestionnaireTargetCompletionDateModal } from '../ServiceInherentRiskQuestionnaireTargetCompletionDateModal/ServiceInherentRiskQuestionnaireTargetCompletionDateModal';

type DisplayedModal =
    | {
          type: 'none';
      }
    | {
          type: 'contacts';
          contacts: ThirdPartyContact[];
          header: string;
      }
    | {
          type: 'users';
          userIds: string[];
          header: string;
      }
    | {
          type: 'editService';
          thirdParty: ThirdPartyResponseWithServices | ThirdPartyWithServicesForNonTprmUser;
          service: ThirdPartyService | ServiceForNonTprmUser;
      }
    | {
          type: 'finalizeThirdParty';
          thirdParty: ThirdPartyResponseWithServices;
      }
    | {
          type: 'finalizeService';
          thirdParty: ThirdPartyResponseWithServices;
          service: ThirdPartyService;
      }
    | {
          type: 'setAssessmentDueDate';
          service: ThirdPartyService;
      }
    | {
          type: 'setIrqTargetCompletionDate';
          service: ThirdPartyService;
      }
    | {
          type: 'submitThirdPartyToBeFinalized';
          thirdParty: ThirdPartyResponseWithServices | ThirdPartyWithServicesForNonTprmUser;
      }
    | {
          type: 'submitServiceToBeFinalized';
          thirdParty: ThirdPartyResponseWithServices | ThirdPartyWithServicesForNonTprmUser;
          service: ThirdPartyService | ServiceForNonTprmUser;
      };

export type UserTypeAndThirdParties =
    | {
          userHasFullTprmAccess: true;
          userId: string;
          thirdParties: ThirdPartyResponseWithServices[];
      }
    | {
          userHasFullTprmAccess: false;
          userId: string;
          thirdParties: ThirdPartyWithServicesForNonTprmUser[];
      };

export interface ThirdPartiesAndServicesListingProps {
    authHandler: AuthHandler;
    tprmApi: ClientTPRMApi;
}

export const ThirdPartiesAndServicesListing = (props: ThirdPartiesAndServicesListingProps) => {
    const [displayedModal, setDisplayedModal] = useState<DisplayedModal>({ type: 'none' });
    const [userTypeAndThirdParties, setUserTypeAndThirdParties] = useState<UserTypeAndThirdParties>();

    // This state will contain `null` if the request for data has completed, but no data exists; or if the user does not have TPRM/Admin privileges.
    const [irqTargetCompletionSetting, setIrqTargetCompletionSetting] = useState<ScheduleV2 | null>();

    const [loadingErrorOccurred, setLoadingErrorOccurred] = useState(false);

    const getThirdParties = useCallback(async (): Promise<void> => {
        try {
            const userRoles = props.authHandler.getUserRoles();
            const userId = props.authHandler.getAuthenticatedUserSubject()!;
            const userHasFullTprmAccess = loggedInUserHasFullAccessToTprm(userRoles);

            if (userHasFullTprmAccess) {
                const thirdParties = (await props.tprmApi.getThirdParties()).data;
                setUserTypeAndThirdParties({ userHasFullTprmAccess: true, userId, thirdParties: thirdParties });
            } else {
                const thirdParties = (await props.tprmApi.getThirdPartiesForNonTprmUser()).data;
                setUserTypeAndThirdParties({ userHasFullTprmAccess: false, userId, thirdParties: thirdParties });
            }
        } catch (error) {
            console.error(error);
            setLoadingErrorOccurred(true);
        }
    }, [props.authHandler, props.tprmApi]);

    const getInherentRiskQuestionnaireTargetCompletion = useCallback(async () => {
        if (loggedInUserHasFullAccessToTprm(props.authHandler.getUserRoles())) {
            try {
                const response = await props.tprmApi.getInherentRiskQuestionnaireTargetCompletion();
                setIrqTargetCompletionSetting(response.data ?? null);
            } catch (error) {
                console.error(error);
                setLoadingErrorOccurred(true);
            }
        } else {
            setIrqTargetCompletionSetting(null);
        }
    }, [props.authHandler, props.tprmApi]);

    useEffect(() => {
        getThirdParties();
        getInherentRiskQuestionnaireTargetCompletion();
    }, [getInherentRiskQuestionnaireTargetCompletion, getThirdParties]);

    if (loadingErrorOccurred) {
        return <Text>{GENERIC_ERROR_MESSAGE}</Text>;
    }

    if (!(userTypeAndThirdParties && irqTargetCompletionSetting !== undefined)) {
        return <Placeholder />;
    }

    const hideModal = (): void => {
        setDisplayedModal({ type: 'none' });
    };

    const manageThirdPartiesAndServicesTableProps: ThirdPartiesAndServicesTableProps = {
        userTypeAndThirdParties,
        onDisplayContacts: (contacts, header) => setDisplayedModal({ type: 'contacts', contacts, header }),
        onDisplayUsers: (userIds, header) => setDisplayedModal({ type: 'users', userIds, header }),
        onConfirmFinalizeThirdParty: (thirdParty) => setDisplayedModal({ type: 'finalizeThirdParty', thirdParty }),
        onConfirmFinalizeService: (thirdParty, service) => setDisplayedModal({ type: 'finalizeService', thirdParty, service }),
        onConfirmSubmitThirdPartyToBeFinalized: (thirdParty) => setDisplayedModal({ type: 'submitThirdPartyToBeFinalized', thirdParty }),
        onConfirmSubmitServiceToBeFinalized: (thirdParty, service) => setDisplayedModal({ type: 'submitServiceToBeFinalized', thirdParty, service }),
        onSetAssessmentDueDate: (service) => setDisplayedModal({ type: 'setAssessmentDueDate', service }),
        onSetIrqTargetCompletionDate: (service) => setDisplayedModal({ type: 'setIrqTargetCompletionDate', service }),
    };

    return (
        <>
            {displayedModal.type === 'contacts' && <ThirdPartyContactModal thirdPartyContacts={displayedModal.contacts} header={displayedModal.header} hideModal={hideModal} />}
            {displayedModal.type === 'users' && <UsersModal userIds={displayedModal.userIds} header={displayedModal.header} hideModal={hideModal} />}

            {displayedModal.type === 'setAssessmentDueDate' && <ServiceAssessmentDueDateModal service={displayedModal.service} hideModal={hideModal} tprmApi={props.tprmApi} serviceAssessmentDueDateSet={getThirdParties} />}
            {displayedModal.type === 'setIrqTargetCompletionDate' && <ServiceInherentRiskQuestionnaireTargetCompletionDateModal hideModal={hideModal} service={displayedModal.service} targetDateSet={getThirdParties} tprmApi={props.tprmApi} />}

            {displayedModal.type === 'finalizeThirdParty' && (
                <ConfirmationModal
                    operationType="nonDestructive"
                    headerText="Finalize Third Party"
                    buttonText="Finalize"
                    buttonLoadingText="Finalizing..."
                    buttonIcon={faCheck}
                    areYouSureText={`Are you sure you want to finalize ${displayedModal.thirdParty.name}?`}
                    performOperation={async () => {
                        await props.tprmApi.finalizeThirdParty(displayedModal.thirdParty.id);
                        await getThirdParties();
                        return 'Third party finalized.';
                    }}
                    hideModal={hideModal}
                >
                    <Text>This third party will no longer be a draft; users who do not have full access to Third-Party Risk Management will no longer be able to edit it. Any of this third party's services that are drafts will remain drafts.</Text>
                </ConfirmationModal>
            )}

            {displayedModal.type === 'finalizeService' && <FinalizeThirdPartyServiceModal action="finalizeExistingService" currentAssessmentWorkflowIrqTargetCompletionDate={displayedModal.service.assessment_workflow_setup.irq_target_completion_date} serviceId={displayedModal.service.id} serviceName={displayedModal.service.name} irqTargetCompletionSetting={irqTargetCompletionSetting ?? undefined} thirdParty={displayedModal.thirdParty} hideModal={hideModal} tprmApi={props.tprmApi} onRequestCompleted={getThirdParties} />}

            {displayedModal.type === 'submitThirdPartyToBeFinalized' && (
                <ConfirmationModal
                    operationType="nonDestructive"
                    headerText="Submit Third Party to be Finalized"
                    buttonText="Submit"
                    buttonLoadingText="Submitting..."
                    buttonIcon={faCheck}
                    areYouSureText={`Are you sure you want to submit ${displayedModal.thirdParty.name} to be finalized?`}
                    performOperation={async () => {
                        await props.tprmApi.submitThirdPartyToBeFinalized(displayedModal.thirdParty.id);
                        await getThirdParties();
                        return 'Third party submitted.';
                    }}
                    hideModal={hideModal}
                >
                    <Text>The manager of this third party will be notified. No third-party services will be affected.</Text>
                </ConfirmationModal>
            )}

            {displayedModal.type === 'submitServiceToBeFinalized' && (
                <ConfirmationModal
                    operationType="nonDestructive"
                    headerText="Submit Third-Party Service to be Finalized"
                    buttonText="Submit"
                    buttonLoadingText="Submitting..."
                    buttonIcon={faCheck}
                    areYouSureText={`Are you sure you want to submit ${displayedModal.service.name} to be finalized?`}
                    performOperation={async () => {
                        await props.tprmApi.submitThirdPartyServiceToBeFinalized(displayedModal.thirdParty.id, displayedModal.service.id);
                        await getThirdParties();
                        return 'Third-party service submitted.';
                    }}
                    hideModal={hideModal}
                >
                    <Text>The manager of this third-party service will be notified.</Text>
                    {displayedModal.thirdParty.finalization_state === FinalizationState.DRAFT_IN_PROGRESS && <Text>This will also submit {displayedModal.thirdParty.name} to be finalized.</Text>}
                </ConfirmationModal>
            )}

            <PageLayoutDefault
                headerTitle="Third Parties and Services"
                headerButtons={
                    <>
                        <Link variant="primaryButton" to={generatePath(CREATE_THIRD_PARTY)} fontAwesomeImage={ICON_ADD_CREATE}>
                            Create Third Party
                        </Link>
                        {userTypeAndThirdParties.thirdParties.length > 0 && (
                            <Link variant="primaryButton" to={generatePath(CREATE_THIRD_PARTY_SERVICE)} fontAwesomeImage={ICON_ADD_CREATE}>
                                Create Third-Party Service
                            </Link>
                        )}
                    </>
                }
                body={[
                    {
                        content: <ThirdPartiesAndServicesTable {...manageThirdPartiesAndServicesTableProps} />,
                    },
                ]}
            />
        </>
    );
};
